// MACHINERY V2 (Stroje) — the hero's live status ribbon (M2.4).
//
// Split out of ProfileHero.tsx for the 300-line cap. Spec: ticket §4.3 (the 9
// canonical states) + artboard 0504 ("● BEŽÍ · od 06:49 · 2h 14m · PRG_42.MPF ·
// 247 ks") and machinery-profile-mash.jsx `MashHeroRibbon`.
//
// >>> DRIVEN EXCLUSIVELY BY deriveCardPresentation. <<<
// Every flag below comes off the M2.1 presentation object: nothing here tests
// `alarms.length`, `conn` or `status`, and the connectivity dot takes
// `presentation.dotConn` (never the raw `conn`).
//
// THE TWO last-known MARKERS ARE DIFFERENT FLAGS, ON PURPOSE:
//   - the STATUS BADGE since-clause takes `staleLook`, because StateCard.tsx
//     (the fleet card, i.e. the contract reference) builds the same clause
//     from `staleLook`. It used to take `alarmLastKnown` here, which is TRUE
//     for offline and stale-alarm but FALSE for a plain-stale machine — so a
//     plain-stale machine printed "· od 06:49" as CURRENT on the hero while
//     its own fleet card printed "(naposledy) 06:49". One machine, two
//     verdicts, out of the same derivation (M2.4 review).
//   - the ALARM STRIP takes `alarmLastKnown`, exactly as StateCard.tsx:276
//     does: an offline machine keeps its offline STATE and shows its alarm as
//     last-known. That one is unchanged.
//
// THE ONE-FOOTER RULE (StateCard's, unchanged): alarm strip OR program line OR
// the no-telemetry note. Never two, never a stacked pair.
//
// An UNCONNECTED machine renders NOTHING here but the no-telemetry note (§4.3:
// no badge, no dot) — the 99%-of-the-fleet case looks like today's profile. A
// DECOMMISSIONED machine renders nothing AT ALL (ruling D) — see below.
//
// No Tailwind, no shadcn, no MUI, no @emotion, no sx, no dt tokens.
import React from "react";

import AlarmStrip from "../fleet/AlarmStrip";
import ConnDot from "../fleet/ConnDot";
import ProgramLine from "../fleet/ProgramLine";
import StatusBadge, { AwaitingBadge } from "../fleet/StatusBadge";
// Moved out of StateCard.tsx at M2.16b step 1b (near-cap split) — see
// fleet/stateCardAtoms.tsx. Same component, same rendering, new home.
import { NoTelemetryFooter } from "../fleet/stateCardAtoms";
import {
  MachineryV2CardPresentation,
  MachineryV2Telemetry,
} from "../fleet/cardStates";

export interface ProfileLiveRibbonProps {
  telemetry: MachineryV2Telemetry;
  presentation: MachineryV2CardPresentation;
  /** A retired machine has NO live surface: the whole ribbon is suppressed. */
  decommissioned: boolean;
}

const ProfileLiveRibbon: React.FC<ProfileLiveRibbonProps> = ({
  telemetry,
  presentation: p,
  decommissioned,
}) => {
  // RULING D (M2.4 review): a retired machine gets NO live ribbon — not a
  // desaturated one, not a "last known" one. MachineProfileV2 also fetches it
  // no telemetry and drops its Live tab, so this is the third of three gates
  // on one rule; it is restated here so the component cannot paint a live
  // state even if a caller hands it stale telemetry mid-transition (active ->
  // decommission -> refresh).
  if (decommissioned) return null;

  const alarm = p.showAlarmStrip ? telemetry.alarms[0] : null;

  return (
    <>
      {p.showStatusRow && (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 10,
            flexWrap: "wrap",
            minWidth: 0,
          }}
        >
          {p.showAwaitingBadge ? (
            <AwaitingBadge />
          ) : (
            <StatusBadge
              status={p.badgeStatus}
              stale={p.staleLook}
              variant="strip"
              showSince={telemetry.since}
              // staleLook, NOT alarmLastKnown — StateCard.tsx:131 builds the
              // same clause from staleLook. See the header.
              lastKnown={p.staleLook}
            />
          )}
          <span style={{ flex: 1 }} />
          <ConnDot state={p.dotConn} lastHeartbeat={telemetry.heartbeat} withLabel />
        </div>
      )}

      {alarm ? (
        <AlarmStrip
          code={alarm.code}
          message={alarm.message}
          since={alarm.since}
          lastKnown={p.alarmLastKnown}
        />
      ) : p.showProgramLine && telemetry.program ? (
        <ProgramLine
          program={telemetry.program}
          partCount={telemetry.partCount}
          spindleRpm={telemetry.spindleRpm}
          feedPercent={telemetry.feedPercent}
        />
      ) : p.showNoTelemetry ? (
        // No `!decommissioned` test any more: a decommissioned machine never
        // reaches this line (early return above).
        <NoTelemetryFooter />
      ) : null}
    </>
  );
};

export default ProfileLiveRibbon;
